import { t } from 'i18next';
import { NavigateFunction } from 'react-router-dom';

import { authService } from 'authOld/services';
import { IUserAutocomplete } from 'authOld/types';
import { ApiStatuses, LocalStorageKeys, NotificationPosition } from 'coreOld/enums';
import { INotification } from 'coreOld/types';
import { goTo, localStorage, objectKeysCamelToSnakeCase } from 'coreOld/utils';

/**
 * Finish a sign-in and put the user where they were headed.
 *
 * `redirect` is the target the server confirmed (site-relative, already
 * validated there). It is followed with a full page load rather than the
 * router because it can point at a legacy page outside the SPA.
 *
 * With no target — switching users, or signing in from the page you are
 * already on — reload in place, as before.
 */
export const login = async (
  navigate: NavigateFunction,
  showNotification: (notification: INotification) => void,
  redirect?: string
) => {
  await loadAuthenticatedUser(showNotification);

  if (redirect) {
    goTo(redirect);

    return;
  }

  navigate(0);
};

const loadAuthenticatedUser = async (showNotification: (notification: INotification) => void) => {
  showNotification({
    message: t('actionProcessInProgress'),
    type: ApiStatuses.LOADING,
    position: NotificationPosition.MODAL,
  });

  try {
    const { record } = await authService.getAuthUser();
    localStorage.set(LocalStorageKeys.LOGGED_USER, JSON.stringify(record));
  } catch (error) {
    localStorage.set(LocalStorageKeys.LOGGED_USER, null);
  }
};

export const getMostRecentUsers = () => {
  const impersonation = localStorage.get(LocalStorageKeys.IMPERSONATION);

  if (!impersonation) {
    return [];
  }

  return JSON.parse(impersonation)?.mostRecentUsed || [];
};

export const saveMostRecentUsers = (user: IUserAutocomplete) => {
  const mostRecentUsed = getMostRecentUsers();

  mostRecentUsed.forEach((recentUser: IUserAutocomplete, index: number) => {
    if (Number(recentUser.id) === Number(user.id)) {
      mostRecentUsed.splice(index, 1);

      return;
    }
  });

  mostRecentUsed.unshift(objectKeysCamelToSnakeCase(user));
  mostRecentUsed.splice(5);

  localStorage.set(LocalStorageKeys.IMPERSONATION, JSON.stringify({ mostRecentUsed }));
};
